import { NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy, ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation
} from '@angular/core';
import { FuseAlertAppearance, FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { FuseUtilsService } from '@fuse/services/utils';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { AlertDisplayService } from './alert-display.service';

@Component({
    selector: 'alert-display',
    styleUrl: './alert-display.component.scss',
    templateUrl: './alert-display.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'alertDisplay',
    imports: [
        FuseAlertComponent,
        TranslocoPipe,
        NgIf
    ],
    standalone: true
})
export class AlertDisplayComponent implements OnInit, OnDestroy {

    private _alertDisplayService = inject(AlertDisplayService);
    private _fuseUtilsService = inject(FuseUtilsService);

    // This controls de ngIf in the template to avoid issues with the fuse-alert displaying itself at certain times.
    public shouldShowAlert: boolean = false;

    @Input() message: string = '';
    @Input() appearance: FuseAlertAppearance = 'outline';
    @Input() dismissed: boolean = true;
    @Input() dismissible: boolean = true;
    @Input() name: string = this._fuseUtilsService.randomId();
    @Input() showIcon: boolean = true;
    @Input() type: FuseAlertType = 'primary';
    @Output() readonly dismissedChanged: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this._alertDisplayService.onTypeChange
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                this.type = value;
            });

        this._alertDisplayService.onMessageChange
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                this.message = value;
            });

        this._alertDisplayService.onDisplayChange
            .pipe(
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                this.shouldShowAlert = value;
                this._changeDetectorRef.markForCheck();
            });
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
